import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BookingPage({ kind }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [item, setItem] = useState(null);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", email: "", details: "", preferred_date: "" });

    const endpoint = kind === "service" ? "services" : "readings";
    const backTo = kind === "service" ? "/services" : "/readings";

    useEffect(() => {
        api.get(`/${endpoint}/${id}`).then((r) => setItem(r.data)).catch(() => navigate(backTo));
    }, [id]);

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: user.name, email: user.email }));
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/checkout/booking", {
                kind, item_id: id, ...form, origin_url: window.location.origin, user_id: user ? user.id : null,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error("Could not start booking. Please try again.");
            setBusy(false);
        }
    };

    if (!item) return <div className="min-h-[60vh] flex items-center justify-center text-gold animate-flicker font-serif text-2xl">Loading…</div>;
    const input = "w-full bg-surface border border-gold/20 rounded-xl px-4 py-3 text-cream placeholder:text-faded focus:outline-none focus:ring-1 focus:ring-gold";

    return (
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16">
            <Link to={backTo} className="inline-flex items-center gap-2 text-ash hover:text-gold mb-8"><ArrowLeft className="w-4 h-4" /> Back</Link>
            <div className="grid md:grid-cols-2 gap-12">
                <div>
                    <img src={item.image} alt={item.name} className="w-full aspect-square object-cover rounded-2xl border border-gold/15" />
                    <div className="mt-6 text-[11px] uppercase tracking-widest text-faded">{item.category} · {item.duration}</div>
                    <h1 className="mt-2 font-serif text-4xl text-cream">{item.name}</h1>
                    <p className="mt-2 text-3xl text-gold">${item.price.toFixed(2)}</p>
                    <p className="mt-5 text-ash leading-relaxed">{item.description}</p>
                </div>

                <form onSubmit={submit} className="p-7 rounded-2xl bg-surface border border-gold/15 h-fit" data-testid="booking-form">
                    <h2 className="font-serif text-2xl text-cream mb-5">{kind === "service" ? "Request this working" : "Book this reading"}</h2>
                    <div className="space-y-4">
                        <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="booking-name" />
                        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="booking-email" />
                        <input type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className={input} data-testid="booking-date" />
                        <textarea rows={4} placeholder="Tell us about your situation…" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className={input} data-testid="booking-details" />
                    </div>
                    <button disabled={busy} data-testid="booking-submit" className="mt-6 w-full bg-gold text-ink rounded-full py-3 font-medium hover:bg-goldbright transition-colors active:scale-95 disabled:opacity-60">
                        {busy ? "Redirecting…" : `Pay $${item.price.toFixed(0)} & Book`}
                    </button>
                    <p className="mt-4 text-xs text-faded text-center">Secure checkout via Stripe. The house will follow up by email.</p>
                </form>
            </div>
        </div>
    );
}
